import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportMeetingDecisionTypes } from './api/meetingDecisionTypeApi'
import Button from '../../components/ui/Button'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import SortSpinner from '../../components/ui/SortSpinner'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { meetingDecisionTypeColumns } from './components/MeetingDecisionTypeColumns'
import MeetingDecisionTypeFormDialog from './components/MeetingDecisionTypeFormDialog'
import { useMeetingDecisionTypes } from './hooks/useMeetingDecisionTypes'
import type { MeetingDecisionType, MeetingDecisionTypeUpsert } from './types'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
// Paylaşılan layout sınıfları (admin-page/admin-form/admin-row-actions).
import '../admin/admin.css'

/**
 * Tutanak Takip → Toplantı Kayıt Tipi yönetim ekranı (032; 042'de <b>salt liste + düzenle</b>). Ekleme /
 * durum (Aktif/Pasif) / silme / ↑↓ sıralama <b>kaldırıldı</b> — kayıt kümesi sabit, yalnız düzenleme.
 * Paylaşılan ui/DataTable (sayfalama kapalı) + ui/FormDialog (düzenle). Başlık = menü adı.
 */
export default function MeetingDecisionTypesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, update, reorder } = useMeetingDecisionTypes()
  const toast = useToast()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: sıralama okları ve düzenle hiç çizilmez.
  const { readOnly } = useProxyMode()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingDecisionType | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip tüm id sırasını backend'e yaz (1..N).
  const onMove = (row: MeetingDecisionType, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    // Hata → hook rollback + global hata toast'ı (tek nokta); ekranda ayrıca toast basılmaz.
    reorder.mutate(orderedIds)
  }

  const openEdit = (row: MeetingDecisionType) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('activity.meetingDecisionTypes.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: MeetingDecisionTypeUpsert) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { ...payload, modDate: editing.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('activity.meetingDecisionTypes.toast.updated'),
            life: 3000,
          })
          setFormOpen(false)
        },
        onError: handleMutationError,
      },
    )
  }

  return (
    <div className="admin-page">
      <MeetingDecisionTypeFormDialog
        visible={formOpen}
        initial={editing}
        submitting={update.isPending}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.tutanak.meetingDecisionTypes')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportMeetingDecisionTypes} disabled={rows.length === 0} />
          </>
        }
      />

      {/* Sabit kayıt kümesi → sayfalama kapalı; yalnız düzenleme. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && (
            <EmptyState
              title={t('activity.meetingDecisionTypes.empty.title')}
              description={t('activity.meetingDecisionTypes.empty.desc')}
            />
          )}
          {!list.isLoading &&
            rows.map((row, rowIndex) => (
              <div key={row.id} className="ui-card">
                {/* Üst satır yalnız yazma kontrolleri taşır → salt okunur kipte hiç çizilmez. */}
                {!readOnly && (
                  <div className="ui-card-top">
                    <span />
                    <div className="ui-card-actions ui-card-actions--compact">
                      <SortSpinner
                        onUp={() => onMove(row, 'up')}
                        onDown={() => onMove(row, 'down')}
                        disableUp={rowIndex === 0}
                        disableDown={rowIndex === rows.length - 1}
                        busy={reorder.isPending}
                        /* Erişilebilir adlar KAYDI da söyler: yinelenen kartlarda yalnız "yukarı
                           taşı"/"düzenle" diyen düğmeler ekran okuyucuda ayırt edilemiyordu (THY AI
                           review bulgusu, 26.10.2026). Görünen başlık `localized(...)` olduğu için
                           erişilebilir ad da onu içeriyor. */
                        labelUp={`${localized(row.nameTr, row.nameEn)}, ${t('common.reorder.up')}`}
                        labelDown={`${localized(row.nameTr, row.nameEn)}, ${t('common.reorder.down')}`}
                      />
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label={`${localized(row.nameTr, row.nameEn)}, ${t('activity.meetingDecisionTypes.actions.edit')}`}
                        onClick={() => openEdit(row)}
                      />
                    </div>
                  </div>
                )}
                <div className="ui-card-head">
                  <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <DataTable
          value={rows}
          loading={list.isLoading}
          paginator={false}
          emptyMessage={
            <EmptyState
              title={t('activity.meetingDecisionTypes.empty.title')}
              description={t('activity.meetingDecisionTypes.empty.desc')}
            />
          }
        >
          {meetingDecisionTypeColumns({
            t,
            localized,
            rowCount: rows.length,
            reordering: reorder.isPending,
            onMove,
            onEdit: openEdit,
            readOnly,
          })}
        </DataTable>
      )}
    </div>
  )
}
